import React, { useState, useEffect, memo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { ArrowDownCircle, CheckCircle } from 'lucide-react';
import { DebouncedInput, DebouncedTextarea } from '../shared/DebouncedInputs';
import api, { formatErrorMessage } from '../../../../utils/api';
import { toast } from 'sonner';

const motivosNC = [
  { value: 'descuento_comercial', label: 'Descuento comercial' },
  { value: 'devolucion', label: 'Devolución de productos/servicios' },
  { value: 'error_facturacion', label: 'Error en facturación' },
  { value: 'bonificacion', label: 'Bonificación' },
  { value: 'otro', label: 'Otro' }
];

const NotaCreditoModal = memo(({ 
  open, 
  onOpenChange, 
  factura,
  onSuccess 
}) => {
  const [notaCredito, setNotaCredito] = useState({
    numero_documento: '',
    amount_clp: '',
    reason: 'descuento_comercial',
    description: '',
    issue_date: new Date().toISOString().slice(0, 10)
  });
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setNotaCredito({
        numero_documento: '',
        amount_clp: '',
        reason: 'descuento_comercial',
        description: '',
        issue_date: new Date().toISOString().slice(0, 10)
      });
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!factura?.id || !notaCredito.amount_clp || !notaCredito.numero_documento) {
      toast.error('Debe ingresar el número de documento y el monto');
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/finanzas/cobranzas-facturas/${factura.id}/nota-credito`, {
        numero_documento: notaCredito.numero_documento,
        amount_clp: parseFloat(notaCredito.amount_clp),
        reason: notaCredito.reason,
        description: notaCredito.description,
        issue_date: notaCredito.issue_date
      });
      
      toast.success('Nota de crédito emitida correctamente');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(formatErrorMessage(error) || 'Error al emitir nota de crédito');
    } finally {
      setLoading(false);
    }
  };

  const formatCLP = (amount) => new Intl.NumberFormat('es-CL').format(amount || 0);

  if (!factura) return null;

  const totalFactura = factura.total_amount_clp || factura.monto_clp || 0;
  const pendiente = factura.pending_amount_clp ?? totalFactura;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <ArrowDownCircle className='w-5 h-5 text-red-600' />
            Emitir Nota de Crédito
          </DialogTitle>
          <DialogDescription>
            Factura #{factura.numero_factura} - {factura.cliente?.company || factura.cliente?.name}
          </DialogDescription>
        </DialogHeader>
        
        <div className='space-y-4'>
          {/* Info de la factura */}
          <Card className='p-3 bg-slate-50 border-slate-200'>
            <div className='grid grid-cols-2 gap-4 text-sm'>
              <div>
                <p className='text-slate-500'>Monto Total Factura</p>
                <p className='font-bold text-slate-900'>${formatCLP(totalFactura)}</p>
              </div>
              <div>
                <p className='text-slate-500'>Saldo Pendiente</p>
                <p className='font-bold text-amber-600'>${formatCLP(pendiente)}</p>
              </div>
            </div>
          </Card>

          {/* Formulario */}
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
              <Label htmlFor='nc_numero'>N° Nota de Crédito *</Label>
              <DebouncedInput
                id='nc_numero'
                placeholder='Ej: NC-001'
                value={notaCredito.numero_documento}
                onChange={(e) => setNotaCredito(prev => ({ ...prev, numero_documento: e.target.value }))}
                data-testid='nc-numero-input'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='nc_amount'>Monto (CLP) *</Label>
              <Input
                id='nc_amount'
                type='number'
                placeholder='0'
                value={notaCredito.amount_clp}
                onChange={(e) => setNotaCredito(prev => ({ ...prev, amount_clp: e.target.value }))}
                data-testid='nc-amount-input'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='nc_date'>Fecha de Emisión</Label>
              <Input
                id='nc_date'
                type='date'
                value={notaCredito.issue_date}
                onChange={(e) => setNotaCredito(prev => ({ ...prev, issue_date: e.target.value }))}
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='nc_reason'>Motivo</Label>
              <Select
                value={notaCredito.reason}
                onValueChange={(value) => setNotaCredito(prev => ({ ...prev, reason: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder='Seleccione motivo' />
                </SelectTrigger>
                <SelectContent>
                  {motivosNC.map((motivo) => (
                    <SelectItem key={motivo.value} value={motivo.value}>
                      {motivo.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='space-y-2'>
            <Label htmlFor='nc_description'>Descripción (opcional)</Label>
            <DebouncedTextarea
              id='nc_description'
              placeholder='Detalle del motivo de la nota de crédito...'
              value={notaCredito.description}
              onChange={(e) => setNotaCredito(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
            />
          </div>
        </div>
        
        <DialogFooter className='gap-2'>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmit} 
            className='bg-red-600 hover:bg-red-700'
            disabled={loading || !notaCredito.amount_clp || !notaCredito.numero_documento}
            data-testid='btn-emitir-nc'
          >
            <CheckCircle className='w-4 h-4 mr-2' />
            {loading ? 'Emitiendo...' : 'Emitir NC'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

NotaCreditoModal.displayName = 'NotaCreditoModal';

export default NotaCreditoModal;
